/** Partner console — shared create/edit organization form (dedicated route).
 *
 * `/partner/organizations/new` and `/partner/organizations/:orgId/edit` both
 * render this. It carries the full org profile: identity, postal address,
 * phone, contact people (multiple, one primary), commercial terms, and a
 * French SIRENE lookup that pre-fills name + address from a SIRET/SIREN. */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { CountrySelector } from "@/components/CountrySelector";
import { PartnerPageHeader } from "@/components/PartnerLayout";
import PhoneInput from "@/components/PhoneInput";
import { Spinner } from "@/components/Spinner";
import { lookupCompany } from "@/lib/partner";
import {
  useCreatePartnerOrg,
  useGrantSignaturePack,
  usePartnerCommercials,
  usePartnerMe,
  usePartnerOrgs,
  useSetPartnerOrgPlan,
  useUpdatePartnerOrg,
} from "@/lib/partner";
import { registrationLabelKey } from "@/lib/registration";
import type { OrgContact, PartnerOrg } from "@/lib/types";
import { DEFAULT_PACK, type PackFields } from "@/modules/billing/packFields";
import { SignaturePackFields } from "@/modules/billing/SignaturePackFields";

/** Contact roles — mirrors backend accounts.OrgContactDesignation. */
const DESIGNATIONS = [
  "president",
  "gerant",
  "ceo",
  "managing_director",
  "director",
  "chairman",
  "owner",
  "partner",
  "administrator",
  "cfo",
  "coo",
  "other",
] as const;

/** A compact set of dial codes for the phone country-code selector. */
type ContactRow = OrgContact & { key: string };

let _rowSeq = 0;
const newRow = (init?: Partial<OrgContact>): ContactRow => ({
  key: `c${_rowSeq++}`,
  id: init?.id,
  name: init?.name ?? "",
  email: init?.email ?? "",
  phone: init?.phone ?? "",
  designation: init?.designation ?? "",
  is_default: init?.is_default ?? false,
});

export default function PartnerOrgFormPage() {
  const { t } = useTranslation();
  const { orgId } = useParams();
  const isEdit = !!orgId;
  const navigate = useNavigate();

  const me = usePartnerMe();
  const orgs = usePartnerOrgs();
  const isReseller = me.data?.kind === "reseller";
  const commercials = usePartnerCommercials(!!isReseller);
  const create = useCreatePartnerOrg();
  const update = useUpdatePartnerOrg();

  const existing: PartnerOrg | undefined = useMemo(
    () => orgs.data?.find((o) => o.id === orgId),
    [orgs.data, orgId],
  );

  if (me.isLoading || (isEdit && orgs.isLoading)) {
    return <Spinner fullscreen label={t("common.loading")} />;
  }

  if (isEdit && !existing && orgs.data) {
    return (
      <>
        <PartnerPageHeader title={t("partner.org.formEditTitle")} />
        <p className="text-sm text-nb-text-3">{t("partner.org.noMatch")}</p>
      </>
    );
  }

  return (
    <OrgForm
      key={orgId ?? "new"}
      isEdit={isEdit}
      existing={existing}
      isReseller={!!isReseller}
      commercials={commercials.data?.commercials ?? []}
      onCancel={() => navigate("/partner/organizations")}
      onSubmit={(payload, handlers) => {
        if (isEdit && existing) {
          update.mutate(
            { orgId: existing.id, patch: payload },
            {
              onSuccess: () => navigate("/partner/organizations"),
              onError: handlers.onError,
            },
          );
        } else {
          create.mutate(payload, {
            onSuccess: () => navigate("/partner/organizations"),
            onError: handlers.onError,
          });
        }
      }}
      pending={create.isPending || update.isPending}
    />
  );
}

/** Plan selector + signature-pack granting for an EXISTING managed org
 * (reseller only). Both hit dedicated endpoints (set-plan / grant-pack),
 * separate from the org profile PATCH — so they apply immediately on their
 * own, not on the form's Save button. Mirrors the inline controls on the
 * organizations list, surfaced here on the Subscription & pricing tab. */
function PlanAndPackSection({ org }: { org: PartnerOrg }) {
  const { t } = useTranslation();
  const setPlan = useSetPartnerOrgPlan();
  const grant = useGrantSignaturePack();
  const [pack, setPack] = useState<PackFields>(DEFAULT_PACK);
  const [granted, setGranted] = useState(false);

  return (
    <section className="card space-y-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("partner.form.planPackTitle")}
      </h2>

      {/* Plan */}
      <div className="space-y-1">
        <label className="text-xs text-nb-text-3" htmlFor="org-plan">
          {t("partner.org.colPlan")}
        </label>
        <div className="flex items-center gap-2">
          <select
            id="org-plan"
            className="input h-9 max-w-xs py-0 text-sm"
            value={org.current_plan_slug || "free"}
            disabled={setPlan.isPending}
            onChange={(e) =>
              setPlan.mutate({ orgId: org.id, planSlug: e.target.value })
            }
          >
            <option value="free">{t("partner.org.planFree")}</option>
            <option value="standard">{t("partner.org.planStandard")}</option>
            <option value="pro">{t("partner.org.planPro")}</option>
          </select>
          {setPlan.isPending && (
            <span className="text-xs text-nb-text-3">{t("common.saving")}</span>
          )}
          {org.pending_plan_request != null && (
            <span
              className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] text-amber-300"
              title={org.pending_plan_request || t("partner.org.planRequested")}
            >
              {t("partner.org.planRequested")}
            </span>
          )}
        </div>
      </div>

      {/* Signature-pack credits + grant */}
      <div className="space-y-2 border-t border-nb-border/60 pt-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-nb-text-3">
            {t("partner.org.colSignatures")}
          </span>
          <span className="font-mono text-xs text-nb-text">
            {org.signature_credits_used ?? 0}/{org.signature_credits_total ?? 0}
          </span>
        </div>
        <SignaturePackFields
          value={pack}
          onChange={(patch) => setPack((f) => ({ ...f, ...patch }))}
          idPrefix={`grant-${org.id}`}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={grant.isPending || pack.quantity < 1}
            onClick={() =>
              grant.mutate(
                { orgId: org.id, ...pack },
                { onSuccess: () => setGranted(true) },
              )
            }
          >
            {grant.isPending
              ? t("common.saving")
              : t("partner.org.grantPackBtn")}
          </button>
          {granted && !grant.isPending && (
            <span className="text-xs text-nb-mint">✓</span>
          )}
        </div>
      </div>
    </section>
  );
}

function OrgForm({
  isEdit,
  existing,
  isReseller,
  commercials,
  onSubmit,
  onCancel,
  pending,
}: {
  isEdit: boolean;
  existing?: PartnerOrg;
  isReseller: boolean;
  commercials: { id: string; name: string }[];
  onSubmit: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any,
    handlers: { onError: (e: unknown) => void },
  ) => void;
  onCancel: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();

  const [name, setName] = useState(existing?.name ?? "");
  const [loginEmail, setLoginEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState(existing?.country ?? "");
  const [line1, setLine1] = useState(existing?.address?.line1 ?? "");
  const [city, setCity] = useState(existing?.address?.city ?? "");
  const [zip, setZip] = useState(existing?.address?.postal_code ?? "");
  const [orgPhone, setOrgPhone] = useState(existing?.phone ?? "");
  const [legalName, setLegalName] = useState(existing?.legal_name ?? "");
  const [regNumber, setRegNumber] = useState(
    existing?.registration_number ?? "",
  );
  const [nafCode, setNafCode] = useState(existing?.naf_code ?? "");
  const [vatId, setVatId] = useState(existing?.vat_id ?? "");
  const [customPrice, setCustomPrice] = useState(
    existing?.custom_price_per_session_eur ?? "",
  );
  const [commissionable, setCommissionable] = useState(
    existing?.commissionable_price_per_session_eur ?? "",
  );
  const [commercialId, setCommercialId] = useState(existing?.commercial_id ?? "");
  const [contacts, setContacts] = useState<ContactRow[]>(() => {
    const rows = (existing?.contacts ?? []).map((c) => newRow(c));
    return rows.length ? rows : [newRow({ is_default: true })];
  });

  const [siren, setSiren] = useState("");
  const [sirenState, setSirenState] = useState<
    { status: "idle" | "loading" | "ok" | "error"; msg?: string }
  >({ status: "idle" });
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"company" | "terms">("company");

  const isFR = (country || "").toUpperCase() === "FR";

  const patchContact = (key: string, patch: Partial<OrgContact>) =>
    setContacts((rows) =>
      rows.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );

  const setDefault = (key: string) =>
    setContacts((rows) =>
      rows.map((r) => ({ ...r, is_default: r.key === key })),
    );

  const addContact = () =>
    setContacts((rows) => [...rows, newRow({ is_default: rows.length === 0 })]);

  const removeContact = (key: string) =>
    setContacts((rows) => {
      const next = rows.filter((r) => r.key !== key);
      if (next.length && !next.some((r) => r.is_default)) {
        return next.map((r, i) => (i === 0 ? { ...r, is_default: true } : r));
      }
      return next;
    });

  const runLookup = async () => {
    if (!siren.trim()) return;
    setSirenState({ status: "loading" });
    try {
      const res = await lookupCompany(siren.trim());
      if (res.legal_name) {
        setLegalName(res.legal_name);
        if (!name) setName(res.legal_name);
      }
      if (res.siret || res.siren) setRegNumber(res.siret || res.siren);
      if (res.naf_code) setNafCode(res.naf_code);
      if (res.address) {
        if (res.address.line1) setLine1(res.address.line1);
        if (res.address.city) setCity(res.address.city);
        if (res.address.postal_code) setZip(res.address.postal_code);
      }
      if (!country) setCountry("FR");
      setSirenState({ status: "ok", msg: t("partner.form.sirenFilled") });
    } catch (e: unknown) {
      const code = (e as { response?: { status?: number } })?.response?.status;
      const msg =
        code === 404
          ? t("partner.form.sirenNotFound")
          : t("partner.form.sirenUnavailable");
      setSirenState({ status: "error", msg });
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    // Required fields live on the Company tab; native validation can't focus
    // them while hidden, so surface the error and switch back to that tab.
    const missingCompany =
      !name.trim() || (!isEdit && (!loginEmail.trim() || password.length < 8));
    if (missingCompany) {
      setTab("company");
      setErr(t("partner.form.fillCompanyFirst"));
      return;
    }
    const phone = orgPhone.trim();
    const cleanContacts = contacts
      .filter((c) => (c.name || "").trim())
      .map((c) => ({
        name: c.name.trim(),
        email: (c.email || "").trim(),
        phone: (c.phone || "").trim(),
        designation: c.designation || "",
        is_default: !!c.is_default,
      }));
    const address = {
      line1: line1.trim(),
      city: city.trim(),
      postal_code: zip.trim(),
      country: (country || "").toUpperCase(),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const common: any = {
      name,
      country: country || undefined,
      phone,
      address,
      contacts: cleanContacts,
      custom_price_per_session_eur: customPrice || null,
      commissionable_price_per_session_eur: commissionable || null,
      legal_name: legalName || undefined,
      registration_number: regNumber || undefined,
      naf_code: nafCode || undefined,
      vat_id: vatId || undefined,
    };

    const payload = isEdit
      ? common
      : {
          ...common,
          admin_email: loginEmail,
          admin_password: password,
          commercial_id: commercialId || null,
        };

    onSubmit(payload, {
      onError: (e2: unknown) => {
        const detail = (e2 as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail;
        setErr(
          detail ??
            (isEdit
              ? t("partner.org.editFailed")
              : t("partner.org.createFailed")),
        );
      },
    });
  };

  return (
    <>
      <PartnerPageHeader
        eyebrow={t("nav.partner.organizations")}
        title={
          isEdit ? t("partner.org.formEditTitle") : t("partner.org.formCreateTitle")
        }
        action={
          <button type="button" className="btn-ghost text-sm" onClick={onCancel}>
            {t("partner.org.backToList")}
          </button>
        }
      />

      <form onSubmit={submit} className="space-y-6">
        <div className="flex gap-1 border-b border-nb-border">
          {(["company", "terms"] as const).map((tb) => (
            <button
              key={tb}
              type="button"
              onClick={() => setTab(tb)}
              data-testid={`org-tab-${tb}`}
              className={
                "-mb-px border-b-2 px-4 py-2 text-sm transition " +
                (tab === tb
                  ? "border-nb-mint text-nb-text"
                  : "border-transparent text-nb-text-3 hover:text-nb-text")
              }
            >
              {t(`partner.form.tab.${tb}`)}
            </button>
          ))}
        </div>

        <div className={tab === "company" ? "space-y-6" : "hidden"}>
        {/* SIRENE lookup — FR only. */}
        {isFR && (
          <section className="card space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
              {t("partner.form.sirenTitle")}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="input flex-1 min-w-[16rem]"
                placeholder={t("partner.form.sirenPlaceholder")}
                value={siren}
                onChange={(e) => setSiren(e.target.value)}
              />
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={runLookup}
                disabled={sirenState.status === "loading" || !siren.trim()}
              >
                {sirenState.status === "loading"
                  ? t("partner.form.sirenFetching")
                  : t("partner.form.sirenFetch")}
              </button>
            </div>
            <p
              className={
                sirenState.status === "error"
                  ? "text-xs text-cvp-danger"
                  : sirenState.status === "ok"
                    ? "text-xs text-nb-mint"
                    : "text-xs text-nb-text-3"
              }
            >
              {sirenState.msg ?? t("partner.form.sirenHint")}
            </p>
          </section>
        )}

        {/* Identity + login. */}
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.org.formCreateTitle")}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-nb-text-3">
              {t("partner.form.companyName")}
              <input
                className="input mt-1"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3">
              {t("partner.org.colCountry")}
              <CountrySelector
                value={country}
                onChange={setCountry}
                allowEmpty
                className="mt-1"
                aria-label={t("partner.org.colCountry")}
              />
            </label>
            {!isEdit && (
              <>
                <label className="text-xs text-nb-text-3">
                  {t("partner.form.loginEmail")}
                  <input
                    className="input mt-1"
                    type="email"
                    required
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                  />
                </label>
                <label className="text-xs text-nb-text-3">
                  {t("partner.form.adminPassword")}
                  <input
                    className="input mt-1"
                    type="password"
                    minLength={8}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </label>
              </>
            )}
          </div>
        </section>

        {/* Address + phone. */}
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.form.addressTitle")}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-nb-text-3 sm:col-span-2">
              {t("partner.form.street")}
              <input
                className="input mt-1"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3">
              {t("partner.form.city")}
              <input
                className="input mt-1"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3">
              {t("partner.form.zip")}
              <input
                className="input mt-1"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3 sm:col-span-2">
              {t("partner.form.phone")}
              <div className="mt-1">
                <PhoneInput
                  value={orgPhone}
                  onChange={setOrgPhone}
                  aria-label={t("partner.form.phone")}
                  placeholder={t("partner.form.phPhone")}
                />
              </div>
            </label>
          </div>
        </section>

        {/* Contact people. */}
        <section className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
              {t("partner.form.contactsTitle")}
            </h2>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={addContact}
            >
              + {t("partner.form.contactAdd")}
            </button>
          </div>
          {contacts.length === 0 && (
            <p className="text-xs text-nb-text-3">
              {t("partner.form.contactNoneHint")}
            </p>
          )}
          <div className="space-y-3">
            {contacts.map((c) => (
              <div
                key={c.key}
                className="rounded-md border border-nb-border p-3 space-y-2"
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    className="input"
                    placeholder={t("partner.form.contactName")}
                    value={c.name}
                    onChange={(e) => patchContact(c.key, { name: e.target.value })}
                  />
                  <select
                    className="input"
                    value={c.designation ?? ""}
                    onChange={(e) =>
                      patchContact(c.key, { designation: e.target.value })
                    }
                    aria-label={t("partner.form.contactDesignation")}
                  >
                    <option value="">{t("partner.designation.none")}</option>
                    {DESIGNATIONS.map((d) => (
                      <option key={d} value={d}>
                        {t(`partner.designation.${d}`)}
                      </option>
                    ))}
                  </select>
                  <div className="flex flex-col gap-1">
                    <input
                      className="input"
                      type="email"
                      placeholder={t("partner.form.contactEmail")}
                      value={c.email ?? ""}
                      onChange={(e) =>
                        patchContact(c.key, { email: e.target.value })
                      }
                    />
                    {!isEdit && loginEmail && (
                      <button
                        type="button"
                        className="self-start text-[11px] text-nb-mint hover:underline"
                        onClick={() => patchContact(c.key, { email: loginEmail })}
                      >
                        {t("partner.form.contactSameAsLogin")}
                      </button>
                    )}
                  </div>
                  <PhoneInput
                    placeholder={t("partner.form.contactPhone")}
                    value={c.phone ?? ""}
                    onChange={(v) => patchContact(c.key, { phone: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-1.5 text-xs text-nb-text-3">
                    <input
                      type="radio"
                      name="default-contact"
                      checked={!!c.is_default}
                      onChange={() => setDefault(c.key)}
                    />
                    {t("partner.form.contactDefault")}
                  </label>
                  {contacts.length > 1 && (
                    <button
                      type="button"
                      className="text-xs text-nb-text-3 hover:text-cvp-danger"
                      onClick={() => removeContact(c.key)}
                    >
                      {t("partner.form.contactRemove")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Legal identity. */}
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.form.legalIdentityTitle")}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-nb-text-3 sm:col-span-2">
              {t("partner.form.legalName")}
              <input
                className="input mt-1"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3">
              {t(registrationLabelKey(country))}
              <input
                className="input mt-1"
                value={regNumber}
                onChange={(e) => setRegNumber(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3">
              {t("partner.form.nafCode")}
              <input
                className="input mt-1"
                placeholder={t("partner.form.phNafCode")}
                value={nafCode}
                onChange={(e) => setNafCode(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3">
              {t("partner.form.vatId")}
              <input
                className="input mt-1"
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
              />
            </label>
          </div>
        </section>
        </div>

        {/* Payment terms / contract tab. */}
        <div className={tab === "terms" ? "space-y-6" : "hidden"}>
        {/* Plan + signature packs — existing managed orgs only (both need an
            org id and hit their own endpoints, not the profile PATCH). */}
        {isEdit && isReseller && existing && (
          <PlanAndPackSection org={existing} />
        )}
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.form.termsTitle")}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-nb-text-3">
              {t("partner.form.customPrice")}
              <input
                className="input mt-1"
                type="number"
                step="0.01"
                min="0"
                placeholder={t("partner.form.phPriceEg")}
                value={customPrice ?? ""}
                onChange={(e) => setCustomPrice(e.target.value)}
              />
            </label>
            <label className="text-xs text-nb-text-3">
              {t("partner.form.bundleCommissionable")}
              <input
                className="input mt-1"
                type="number"
                step="0.01"
                min="0"
                placeholder={t("partner.form.phFullPrice")}
                value={commissionable ?? ""}
                onChange={(e) => setCommissionable(e.target.value)}
              />
            </label>
            {!isEdit && isReseller && commercials.length > 0 && (
              <label className="text-xs text-nb-text-3 sm:col-span-2">
                {t("partner.form.attribute")}
                <select
                  className="input mt-1"
                  value={commercialId ?? ""}
                  onChange={(e) => setCommercialId(e.target.value)}
                >
                  <option value="">{t("partner.form.attributeNone")}</option>
                  {commercials.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </section>
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary text-sm" disabled={pending}>
            {pending ? t("common.saving") : t("partner.org.saveOrg")}
          </button>
          <button type="button" className="btn-ghost text-sm" onClick={onCancel}>
            {t("common.cancel")}
          </button>
          {err && <span className="text-xs text-cvp-danger">{err}</span>}
        </div>
      </form>
    </>
  );
}
